import { useEffect, useState } from "react";
import { Plus, X, ArrowDown, GitBranch } from "lucide-react";
import { api } from "@/lib/api";

const DOWNLOAD_FORMATS = [
  { id: "print_cmyk_pdf", label: "Print-ready CMYK PDF" },
  { id: "web_rgb_pdf", label: "Web / RGB PDF" },
  { id: "jpg", label: "JPG image" },
  { id: "png", label: "PNG image" },
];

const uid = () => `tmp_${Math.random().toString(36).slice(2, 10)}`;

export default function ProgramPolicies({ brandId, program, onSaved }) {
  const initial = program.policies || {};
  const [locations, setLocations] = useState([]);
  const [members, setMembers] = useState([]);
  const [eligible, setEligible] = useState(initial.eligible_location_ids || []);
  const [steps, setSteps] = useState(
    (initial.approval_steps || []).map((s) => ({ ...s, _key: s.step_id || uid() }))
  );
  const [formats, setFormats] = useState(
    initial.download_formats?.length ? initial.download_formats : ["print_cmyk_pdf"]
  );
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get(`/brands/${brandId}/locations`).catch(() => ({ data: [] })),
      api.get(`/brands/${brandId}/members`).catch(() => ({ data: [] })),
    ]).then(([l, m]) => {
      setLocations(l.data || []);
      setMembers(m.data || []);
    });
  }, [brandId]);

  const toggle = (arr, setArr, id) =>
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const addStep = () =>
    setSteps((s) => [...s, { _key: uid(), name: "", approver_user_ids: [], relation_to_previous: "sequential" }]);
  const removeStep = (key) => setSteps((s) => s.filter((x) => x._key !== key));
  const patchStep = (key, patch) =>
    setSteps((s) => s.map((x) => (x._key === key ? { ...x, ...patch } : x)));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch(`/brands/${brandId}/programs/${program.program_id}/policies`, {
        eligible_location_ids: eligible,
        download_formats: formats,
        approval_steps: steps.map(({ _key, ...rest }) => rest),
      });
      onSaved?.(data);
      setSteps((data.policies?.approval_steps || []).map((s) => ({ ...s, _key: s.step_id || uid() })));
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save policies");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`program-policies-${program.program_id}`}
      className="border border-[#1A1F2C]/10 bg-white rounded-lg p-5 space-y-6"
    >
      {/* Access — eligible locations */}
      <section>
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6358] mb-1">Access · eligible locations</p>
        <p className="text-xs text-[#6B6358] mb-2">
          Marketers only see this program for an eligible location. Leave all unchecked = every location is eligible.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {locations.length === 0 && <span className="text-xs text-[#6B6358]">No locations yet.</span>}
          {locations.map((l) => {
            const on = eligible.includes(l.location_id);
            return (
              <button
                key={l.location_id}
                type="button"
                data-testid={`policies-location-${l.location_id}`}
                onClick={() => toggle(eligible, setEligible, l.location_id)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  on ? "bg-[#1F6B6B] text-white border-[#1F6B6B]" : "bg-[#F8F5EE] text-[#3D3A35] border-[#1A1F2C]/20"
                }`}
              >
                {l.name}
              </button>
            );
          })}
        </div>
      </section>

      {/* Review workflow — approval steps */}
      <section>
        <div className="flex items-center justify-between mb-1">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6358]">Review workflow · approval steps</p>
          <button
            type="button"
            data-testid={`policies-add-step-${program.program_id}`}
            onClick={addStep}
            className="text-[10px] uppercase tracking-[0.14em] inline-flex items-center gap-1 border border-[#1A1F2C]/20 rounded-full px-2.5 py-1"
          >
            <Plus size={12} /> Add step
          </button>
        </div>
        <p className="text-xs text-[#6B6358] mb-2">
          Submitters are any user from an eligible location. Approvers default to all brand admins — name specific
          users to restrict a step to them.
        </p>
        <div className="space-y-3">
          {steps.length === 0 && <p className="text-xs text-[#6B6358]">No steps — submission is auto-approved.</p>}
          {steps.map((st, i) => (
            <div key={st._key} data-testid={`policies-step-${i}`} className="border border-[#1A1F2C]/10 rounded-md p-3">
              {i > 0 && (
                <div className="flex gap-1.5 mb-2">
                  {["sequential", "concurrent"].map((rel) => {
                    const on = st.relation_to_previous === rel;
                    const Icon = rel === "sequential" ? ArrowDown : GitBranch;
                    return (
                      <button
                        key={rel}
                        type="button"
                        data-testid={`policies-step-${i}-rel-${rel}`}
                        onClick={() => patchStep(st._key, { relation_to_previous: rel })}
                        className={`text-[10px] uppercase tracking-[0.12em] inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${
                          on ? "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]" : "bg-[#F8F5EE] text-[#6B6358] border-[#1A1F2C]/20"
                        }`}
                      >
                        <Icon size={10} /> {rel === "sequential" ? "After previous" : "With previous"}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <input
                  data-testid={`policies-step-${i}-name`}
                  value={st.name}
                  onChange={(e) => patchStep(st._key, { name: e.target.value })}
                  placeholder={`Step ${i + 1} name (e.g. Local review)`}
                  className="flex-1 text-sm px-3 py-1.5 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
                />
                <button
                  type="button"
                  data-testid={`policies-step-${i}-remove`}
                  onClick={() => removeStep(st._key)}
                  className="p-1.5 text-[#B5462F] hover:text-[#8f3624]"
                  title="Remove step"
                >
                  <X size={15} />
                </button>
              </div>
              <p className="text-[10px] uppercase tracking-[0.14em] text-[#6B6358] mb-1">
                Approvers {st.approver_user_ids.length === 0 ? "(all brand admins)" : `(${st.approver_user_ids.length} named)`}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {members.map((m) => {
                  const on = st.approver_user_ids.includes(m.user_id);
                  return (
                    <button
                      key={m.user_id}
                      type="button"
                      data-testid={`policies-step-${i}-approver-${m.user_id}`}
                      onClick={() =>
                        patchStep(st._key, {
                          approver_user_ids: on
                            ? st.approver_user_ids.filter((x) => x !== m.user_id)
                            : [...st.approver_user_ids, m.user_id],
                        })
                      }
                      className={`px-2.5 py-0.5 text-[11px] rounded-full border transition-colors ${
                        on ? "bg-[#C28A2E] text-white border-[#C28A2E]" : "bg-[#F8F5EE] text-[#3D3A35] border-[#1A1F2C]/20"
                      }`}
                      title={m.email}
                    >
                      {m.name || m.email} · {m.role}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Download formats */}
      <section>
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B6358] mb-2">Download formats</p>
        <div className="flex flex-wrap gap-1.5">
          {DOWNLOAD_FORMATS.map((f) => {
            const on = formats.includes(f.id);
            return (
              <button
                key={f.id}
                type="button"
                data-testid={`policies-format-${f.id}`}
                onClick={() => toggle(formats, setFormats, f.id)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  on ? "bg-[#1F6B6B] text-white border-[#1F6B6B]" : "bg-[#F8F5EE] text-[#3D3A35] border-[#1A1F2C]/20"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </section>

      {error && <p className="text-xs text-[#B5462F]" data-testid="policies-error">{error}</p>}
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid={`policies-save-${program.program_id}`}
          onClick={save}
          disabled={saving}
          className="px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] bg-[#1A1F2C] text-[#F8F5EE] rounded-full disabled:opacity-50"
        >
          {saving ? "Saving…" : savedOk ? "Saved ✓" : "Save policies"}
        </button>
      </div>
    </div>
  );
}
